/** Midnight on the local calendar day of `date`. */
export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** The latest day the screening window may start: yesterday. */
export function latestScreeningDate(now: Date = new Date()): Date {
  const date = startOfLocalDay(now);
  date.setDate(date.getDate() - 1);
  return date;
}

/** True when `date` falls on a calendar day before `now`. Today is not in the past. */
export function isPastCalendarDate(date: Date | null | undefined, now: Date = new Date()): boolean {
  if (!date || Number.isNaN(date.getTime())) {
    return false;
  }
  return startOfLocalDay(date).getTime() < startOfLocalDay(now).getTime();
}

/**
 * UTC midnight of the local calendar day.
 *
 * `HttpClient` serialises `Date` with `toISOString()`. A local midnight in a
 * positive offset becomes the previous UTC day, so the launch request would
 * post a different date than the one the user picked.
 */
export function toScreenUntilInstant(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

export type ScreeningLookbackPreset = '30d' | '3m' | '6m' | '12m';

const SCREENING_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Calendar day one year before `today`. */
export function oneYearBefore(today: Date): Date {
  const start = startOfLocalDay(today);
  start.setFullYear(start.getFullYear() - 1);
  return start;
}

/** The screen-from day for a preset, measured back from today. */
export function lookbackStart(preset: ScreeningLookbackPreset, today: Date): Date {
  const start = startOfLocalDay(today);
  if (preset === '30d') {
    start.setDate(start.getDate() - 30);
    return start;
  }
  const months = preset === '3m' ? 3 : preset === '6m' ? 6 : 12;
  start.setMonth(start.getMonth() - months);
  return start;
}

/** Whole days from `from` until `to`. */
export function daysBetween(from: Date, to: Date): number {
  const ms = startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime();
  return Math.round(ms / 86400000);
}

/** Where the filled bar sits on a one-year track that ends today. */
export function timelineOffset(from: Date, today: Date): { left: number; width: number } {
  const end = startOfLocalDay(today);
  const start = oneYearBefore(today);
  const span = end.getTime() - start.getTime();
  if (span <= 0) {
    return { left: 0, width: 100 };
  }
  const fromTime = Math.min(end.getTime(), Math.max(start.getTime(), startOfLocalDay(from).getTime()));
  const left = Math.round(((fromTime - start.getTime()) / span) * 100);
  return { left, width: 100 - left };
}

export function formatScreeningDay(date: Date): string {
  return `${date.getDate()} ${SCREENING_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function matchingLookbackPreset(from: Date, today: Date): ScreeningLookbackPreset | null {
  const day = startOfLocalDay(from).getTime();
  const presets: ScreeningLookbackPreset[] = ['30d', '3m', '6m', '12m'];
  return presets.find((preset) => lookbackStart(preset, today).getTime() === day) ?? null;
}
